import crypto from 'crypto';
import type { PinterestEventData, PinterestUserData, PinterestEvent, PinterestEventName } from '../app/types/pintrest'

const PINTEREST_TAG_ID = process.env.NEXT_PUBLIC_PINTEREST_TAG_ID;
  const PINTEREST_API_TOKEN = process.env.PINTEREST_API_TOKEN;
  const PINTEREST_AD_ACCOUNT_ID = process.env.PINTEREST_AD_ACCOUNT_ID;
  
  // Extend Window interface for pintrk
  declare global {
    interface Window {
      pintrk?: {
        (command: 'load' | 'page', ...args: any[]): void;
        (command: 'track', eventName: string, data?: any): void;
        queue?: any[];
      };
    }
  }
  
  // Hash user data for privacy
  export function hashUserData(data: string | undefined): string | undefined {
    if (!data) return undefined;
    return crypto.createHash('sha256').update(data.toLowerCase().trim()).digest('hex');
  }
  
  // Client-side tracking
  export function trackClientEvent(eventName: PinterestEventName, eventData: PinterestEventData = {}): void {
    if (typeof window !== 'undefined' && window.pintrk) {
      window.pintrk('track', eventName, eventData);
    }
  }
  
  // Server-side tracking via API route
  export async function trackServerEvent(
    eventName: PinterestEventName, 
    eventData: PinterestEventData = {}, 
    userData: PinterestUserData = {}
  ): Promise<{ success: boolean; data?: any }> {
    try {
      const response = await fetch('/api/pinterest-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventName, eventData, userData }),
      });
      return await response.json();
    } catch (error) {
      console.error('Server tracking failed:', error);
      return { success: false };
    }
  }
  
  // Unified tracking - does both client and server
  export async function trackEvent(
    eventName: PinterestEventName, 
    eventData: PinterestEventData = {}, 
    userData: PinterestUserData = {}
  ): Promise<void> {
    // Always track client-side first (immediate)
    trackClientEvent(eventName, eventData);
    
    // Then track server-side (reliable)
    if (userData.email || userData.userId) {
      await trackServerEvent(eventName, eventData, userData);
    }
  }
  
  // Content category detection
  function detectCategory(query: string): string {
    const q = query.toLowerCase();
    if (q.includes('romance') || q.includes('love')) return 'romance';
    if (q.includes('thriller') || q.includes('dark')) return 'thriller';
    if (q.includes('fantasy') || q.includes('magic')) return 'fantasy';
    if (q.includes('sci-fi') || q.includes('science')) return 'scifi';
    return 'general';
  }
  
  // Specific events for 
  export const pinterest = {
    trackSearch: async (query: string, userData: PinterestUserData = {}): Promise<void> => {
      const eventData: PinterestEventData = {
        search_query: query,
        content_category: detectCategory(query),
        value: 1.00,
        currency: 'USD',
      };
      await trackEvent('search', eventData, userData);
    },
  
    trackQualityUser: async (searchCount: number, userData: PinterestUserData = {}): Promise<void> => {
      const eventData: PinterestEventData = {
        value: searchCount * 2.00,
        order_quantity: searchCount,
        custom_label: 'engaged_user',
      };
      await trackEvent('custom', eventData, userData);
    },
  
    trackBookView: async (bookId: string, bookTitle: string, category: string, userData: PinterestUserData = {}): Promise<void> => {
      const eventData: PinterestEventData = {
        content_ids: [bookId],
        content_name: bookTitle,
        content_category: category,
        content_type: 'product',
        value: 1.50,
        currency: 'USD',
      };
      await trackEvent('viewcategory', eventData, userData);
    },
  
    trackSignup: async (email: string, method: string = 'email'): Promise<void> => {
      const eventData: PinterestEventData = {
        value: 10.00,
        currency: 'USD',
        lead_type: method,
      };
      await trackEvent('signup', eventData, { email });
    },
  };
  